import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { GithubTokenService } from '../../services/github-token.service';
import { NotificationService } from '../../services/notification.service';

interface TokenMutation {
  tokenSet: boolean;
  successKey: string;
}

let instanceCount = 0;

@Component({
  selector: 'app-github-token-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, MatIcon],
  templateUrl: './github-token-section.component.html',
  styleUrl: './github-token-section.component.scss',
})
export class GithubTokenSectionComponent {
  private readonly githubTokenService = inject(GithubTokenService);
  private readonly notifications = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly hasToken = input.required<boolean>();

  readonly tokenSet = linkedSignal(() => this.hasToken());
  readonly draft = signal('');
  readonly busy = signal(false);

  protected readonly inputId = `github-token-input-${++instanceCount}`;

  onDraftInput(event: Event): void {
    this.draft.set((event.target as HTMLInputElement).value);
  }

  save(): void {
    const apiToken = this.draft().trim();
    if (!apiToken || this.busy()) return;

    this.run(this.githubTokenService.save(apiToken), { tokenSet: true, successKey: 'githubToken.snack.saved' });
  }

  remove(): void {
    if (this.busy()) return;

    this.run(this.githubTokenService.delete(), { tokenSet: false, successKey: 'githubToken.snack.deleted' });
  }

  private run(request$: Observable<void>, mutation: TokenMutation): void {
    this.busy.set(true);
    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.draft.set('');
        this.tokenSet.set(mutation.tokenSet);
        this.busy.set(false);
        this.notifications.success(mutation.successKey);
      },
      error: () => {
        this.busy.set(false);
        this.notifications.error('githubToken.snack.error');
      },
    });
  }
}
